import React, { useEffect, useState } from 'react';
import { renderRichText, storyblokEditable } from 'gatsby-source-storyblok';
import S from './Form.styled';
import Input, { IInputBlok } from '../Input';
import { Flex } from '@/styles/global';
import { BreadSmall, FormTitle } from '@/styles/typography';
import { ITermsCheckboxProps } from '../TermsCheckbox';
import InputCheckbox from '@/components/global/InputCheckbox';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { StyledButton } from '@/components/global';
import getUtmTags from '@/src/helpers/getUTMTags';

export type FormData = {
  [key: string]: string | boolean | { [key: string]: string };
};

type FormState = {
  isLoading: boolean;
  error?: string;
  data: FormData;
  success: boolean;
};

export const emptyForm = (): FormData => ({});

const emptyFormState = (): FormState => ({
  isLoading: false,
  data: emptyForm(),
  success: false,
});

export interface IForm {
  title_small: string;
  form_name: string;
  inputs: IInputBlok[];
  terms: ITermsCheckboxProps[];
  text_above_button: string;
}
export interface IFormProps {
  blok: IForm;
  noTermsTitle?: boolean;
  wrap?: boolean;
  onSuccess?: () => void;
}

const Form: React.FC<IFormProps> = ({
  blok,
  blok: { title_small, inputs, terms, form_name, text_above_button },
  noTermsTitle = false,
  wrap = true,
  onSuccess,
}) => {
  const [formState, setFormState] = useState(emptyFormState());

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>();

  // Set formName
  useEffect(() => {
    setFormState((state) => ({
      ...state,
      data: {
        ...state.data,
        formName: form_name,
      },
    }));
  }, []);

  // Update form state
  const updateFormProperty = <K extends keyof FormData>(
    property: K,
    value: FormData[K]
  ): void => {
    setFormState((state: FormState) => ({
      ...state,
      data: {
        ...state.data,
        [property]: value,
      },
    }));
  };

  // Submit
  const submitForm = handleSubmit(() => {
    const utmParms = getUtmTags() || {};

    setFormState((state) => ({
      ...state,
      isLoading: true,
    }));

    axios
      .post(process.env.GATSBY_ZAPIER_WEBHOOK_URL || '', {
        ...formState.data,
        utmParms,
      })
      .then(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'formSubmit',
          form_name: form_name,
        });

        setFormState({ isLoading: false, success: true, data: emptyForm() });

        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      })
      .catch((error) => {
        console.error('Form submission failed', error);
        setFormState({
          isLoading: false,
          error: 'Something went wrong',
          data: emptyForm(),
          success: false,
        });
      });
  });

  return (
    <S.Form {...storyblokEditable(blok)} $column onSubmit={submitForm}>
      {title_small && (
        <Flex>
          <FormTitle>{title_small}</FormTitle>
        </Flex>
      )}
      <S.Wrapper $wrap={wrap}>
        {inputs.map((input, i) => {
          return (
            <div key={i}>
              <Input
                blok={{
                  key: input.key || '',
                  label: input.label || '',
                  placeholder: input.placeholder || '',
                  type: input.type || 'text',
                  required: input.required || false,
                }}
                register={register(input.key, {
                  required: input.required || false,
                })}
                onInputChange={(key, value) => updateFormProperty(key, value)}
                error={errors[input.key]}
                setValue={setValue}
              />
              {errors[input.key] && (
                <BreadSmall>This field is required</BreadSmall>
              )}
            </div>
          );
        })}
      </S.Wrapper>
      <S.Wrapper>
        {!noTermsTitle && terms && terms.length > 0 && (
          <Flex $margin={'16px 0 0'}>
            <FormTitle>Terms and conditions</FormTitle>
          </Flex>
        )}
        {terms?.map((term, i) => {
          return (
            <Flex $column key={i} $gap={2}>
              <Flex $align={'center'} $gap={8} key={i}>
                <InputCheckbox
                  checked={formState.data[term.key] as boolean}
                  register={register(term.key, {
                    required: term.required || false,
                  })}
                  onChange={(e) => {
                    updateFormProperty(term.key, e.target.checked);
                  }}
                  error={errors[term.key]}
                />
                <S.Label
                  dangerouslySetInnerHTML={{
                    __html: renderRichText(term.label),
                  }}
                />
              </Flex>
              {errors[term.key] && (
                <BreadSmall $margin={'0 0 0 30px'}>
                  This field is required
                </BreadSmall>
              )}
            </Flex>
          );
        })}
      </S.Wrapper>
      {blok.text_above_button && (
        <Flex $margin={'40px 0 0'}>
          <BreadSmall>{text_above_button}</BreadSmall>
        </Flex>
      )}
      <StyledButton
        label="Submit"
        margin="40px 0 0 0"
        noArrow
        fullWidth
        center
      />
    </S.Form>
  );
};

export default Form;
